import { useCallback, useEffect, useState, type FormEvent } from "react";
import {
  approveApplication,
  fetchApplications,
  rejectApplication,
  type AdminApplication,
  type ApplicationStatus,
  type ApproveResult,
} from "@/api/admin";
import { apiErrorMessage } from "@/api/client";
import type { Pagination } from "@/api/types";
import { formatDateTime } from "@/utils/format";

const STATUS_OPTIONS: { value: ApplicationStatus | ""; label: string }[] = [
  { value: "", label: "すべて" },
  { value: "pending", label: "審査待ち" },
  { value: "approved", label: "承認済み" },
  { value: "rejected", label: "却下" },
];

const STATUS_BADGE: Record<ApplicationStatus, string> = {
  pending: "badge-yellow",
  approved: "badge-green",
  rejected: "badge-red",
};

const STATUS_LABEL: Record<ApplicationStatus, string> = {
  pending: "審査待ち",
  approved: "承認済み",
  rejected: "却下",
};

export function AdminApplicationsPage() {
  const [apps, setApps] = useState<AdminApplication[]>([]);
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    per_page: 20,
    total: 0,
    total_pages: 0,
  });
  const [statusFilter, setStatusFilter] = useState<ApplicationStatus | "">("pending");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<{ type: "ok" | "err"; text: string } | null>(null);

  const [selected, setSelected] = useState<AdminApplication | null>(null);
  const [approvedResult, setApprovedResult] = useState<ApproveResult | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchApplications(statusFilter || undefined, page, 20);
      setApps(res.applications);
      setPagination(res.pagination);
    } catch (e) {
      setError(apiErrorMessage(e, "申込一覧の取得に失敗しました"));
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold">申込管理</h1>

      <div className="flex items-center gap-3">
        <label className="label m-0" htmlFor="status">状態</label>
        <select
          id="status"
          className="input w-48"
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value as ApplicationStatus | "");
            setPage(1);
          }}
        >
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      {flash && (
        <div
          className={`rounded-md px-4 py-3 text-sm border ${
            flash.type === "ok"
              ? "bg-green-50 border-green-200 text-green-800"
              : "bg-red-50 border-red-200 text-red-800"
          }`}
        >
          {flash.text}
        </div>
      )}

      {error && (
        <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-center py-12 text-gray-500">読み込み中...</div>
      ) : apps.length === 0 ? (
        <div className="card p-8 text-center text-gray-500">該当する申込はありません。</div>
      ) : (
        <div className="card overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-3 py-2 text-left font-medium">申込日時</th>
                <th className="px-3 py-2 text-left font-medium">企業名</th>
                <th className="px-3 py-2 text-left font-medium">担当者</th>
                <th className="px-3 py-2 text-left font-medium">メール</th>
                <th className="px-3 py-2 text-left font-medium">希望プラン</th>
                <th className="px-3 py-2 text-center font-medium">状態</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {apps.map((a) => (
                <tr key={a.id} className="hover:bg-gray-50">
                  <td className="px-3 py-2 text-gray-600">{formatDateTime(a.created_at)}</td>
                  <td className="px-3 py-2 font-medium">{a.company_name}</td>
                  <td className="px-3 py-2">{a.contact_name}</td>
                  <td className="px-3 py-2 font-mono text-xs">{a.email}</td>
                  <td className="px-3 py-2 uppercase">{a.desired_plan}</td>
                  <td className="px-3 py-2 text-center">
                    <span className={STATUS_BADGE[a.status]}>
                      {STATUS_LABEL[a.status]}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      type="button"
                      className="text-brand-700 hover:underline"
                      onClick={() => setSelected(a)}
                    >
                      詳細
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {pagination.total_pages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button
            type="button"
            className="btn-secondary"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
          >
            前へ
          </button>
          <div className="text-sm">
            {pagination.page} / {pagination.total_pages}（{pagination.total} 件）
          </div>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => setPage((p) => Math.min(pagination.total_pages, p + 1))}
            disabled={page >= pagination.total_pages}
          >
            次へ
          </button>
        </div>
      )}

      {selected && (
        <ApplicationDetailDialog
          application={selected}
          onClose={() => setSelected(null)}
          onApproved={(res) => {
            setSelected(null);
            setApprovedResult(res);
            setFlash({
              type: "ok",
              text: `「${res.tenant.name}」を承認し、顧客アカウントを作成しました`,
            });
            void load();
          }}
          onRejected={() => {
            setSelected(null);
            setFlash({ type: "ok", text: "申込を却下しました" });
            void load();
          }}
          onError={(msg) => setFlash({ type: "err", text: msg })}
        />
      )}

      {approvedResult && (
        <ApprovedCredentialsDialog
          result={approvedResult}
          onClose={() => setApprovedResult(null)}
        />
      )}
    </div>
  );
}

// ---------- Dialogs ----------

function DialogShell({
  title,
  children,
  onClose,
  maxWidth = "max-w-xl",
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
  maxWidth?: string;
}) {
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className={`bg-white rounded-lg shadow-lg w-full ${maxWidth} max-h-full overflow-auto`}>
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="font-semibold">{title}</h2>
          <button type="button" className="text-gray-500 hover:text-gray-700" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}

function ApplicationDetailDialog({
  application,
  onClose,
  onApproved,
  onRejected,
  onError,
}: {
  application: AdminApplication;
  onClose: () => void;
  onApproved: (res: ApproveResult) => void;
  onRejected: () => void;
  onError: (msg: string) => void;
}) {
  const [mode, setMode] = useState<"view" | "approve" | "reject">("view");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const pending = application.status === "pending";

  const onSubmitApprove = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const params: { note?: string } = {};
      if (note.trim()) params.note = note.trim();
      const res = await approveApplication(application.id, params);
      onApproved(res);
    } catch (e) {
      onError(apiErrorMessage(e, "承認に失敗しました"));
    } finally {
      setSubmitting(false);
    }
  };

  const onSubmitReject = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await rejectApplication(application.id, {
        note: note.trim() || undefined,
      });
      onRejected();
    } catch (e) {
      onError(apiErrorMessage(e, "却下に失敗しました"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DialogShell title={`申込詳細: ${application.company_name}`} onClose={onClose}>
      <dl className="grid grid-cols-3 gap-2 text-sm mb-4">
        <dt className="text-gray-500">企業名</dt>
        <dd className="col-span-2">{application.company_name}</dd>
        <dt className="text-gray-500">担当者</dt>
        <dd className="col-span-2">{application.contact_name}</dd>
        <dt className="text-gray-500">メール</dt>
        <dd className="col-span-2 font-mono text-xs">{application.email}</dd>
        <dt className="text-gray-500">電話</dt>
        <dd className="col-span-2">{application.phone || "-"}</dd>
        <dt className="text-gray-500">希望プラン</dt>
        <dd className="col-span-2 uppercase">{application.desired_plan}</dd>
        <dt className="text-gray-500">発送先</dt>
        <dd className="col-span-2">
          {application.shipping_postal_code && `〒${application.shipping_postal_code} `}
          {application.shipping_address || "-"}
        </dd>
        <dt className="text-gray-500">事業内容</dt>
        <dd className="col-span-2 whitespace-pre-wrap">
          {application.business_description || "-"}
        </dd>
        <dt className="text-gray-500">申込日時</dt>
        <dd className="col-span-2">{formatDateTime(application.created_at)}</dd>
        <dt className="text-gray-500">状態</dt>
        <dd className="col-span-2">
          <span className={STATUS_BADGE[application.status]}>
            {STATUS_LABEL[application.status]}
          </span>
          {application.reviewed_at && (
            <span className="ml-2 text-xs text-gray-500">
              審査: {formatDateTime(application.reviewed_at)}
            </span>
          )}
        </dd>
        {application.review_note && (
          <>
            <dt className="text-gray-500">審査メモ</dt>
            <dd className="col-span-2 whitespace-pre-wrap">{application.review_note}</dd>
          </>
        )}
      </dl>

      {pending && mode === "view" && (
        <div className="border-t pt-4 flex justify-end gap-2">
          <button type="button" className="btn-secondary" onClick={onClose}>
            閉じる
          </button>
          <button
            type="button"
            className="btn-danger"
            onClick={() => setMode("reject")}
          >
            却下
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={() => setMode("approve")}
          >
            承認
          </button>
        </div>
      )}

      {pending && mode === "approve" && (
        <form onSubmit={onSubmitApprove} className="border-t pt-4 space-y-3">
          <div className="text-sm text-gray-700">
            この申込を承認すると、企業名「{application.company_name}」のテナントが作成され、
            メール「{application.email}」宛にご利用開始のご案内が送信されます。
          </div>
          <div className="rounded-md bg-blue-50 border border-blue-200 px-3 py-2 text-xs text-blue-800">
            パスワードはメール本文には記載されません。顧客はメール内の
            「パスワード設定 URL」（3 日間有効）から、ご自身でパスワードを設定します。
          </div>
          <div>
            <label className="label">審査メモ（任意）</label>
            <textarea
              className="input h-20 resize-none"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              disabled={submitting}
            />
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setMode("view")}
              disabled={submitting}
            >
              戻る
            </button>
            <button type="submit" className="btn-primary" disabled={submitting}>
              {submitting ? "処理中..." : "承認して顧客アカウント作成"}
            </button>
          </div>
        </form>
      )}

      {pending && mode === "reject" && (
        <form onSubmit={onSubmitReject} className="border-t pt-4 space-y-3">
          <div className="text-sm text-gray-700">
            この申込を却下します。申込者に却下メールが自動送信されます。
          </div>
          <div>
            <label className="label">却下理由・備考（任意）</label>
            <textarea
              className="input h-20 resize-none"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              disabled={submitting}
            />
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setMode("view")}
              disabled={submitting}
            >
              戻る
            </button>
            <button type="submit" className="btn-danger" disabled={submitting}>
              {submitting ? "処理中..." : "却下する"}
            </button>
          </div>
        </form>
      )}

      {!pending && (
        <div className="border-t pt-4 flex justify-end">
          <button type="button" className="btn-secondary" onClick={onClose}>
            閉じる
          </button>
        </div>
      )}
    </DialogShell>
  );
}

function ApprovedCredentialsDialog({
  result,
  onClose,
}: {
  result: ApproveResult;
  onClose: () => void;
}) {
  return (
    <DialogShell
      title="承認完了"
      onClose={onClose}
      maxWidth="max-w-md"
    >
      <div className="space-y-3 text-sm">
        <div className="rounded-md bg-green-50 border border-green-200 px-3 py-2 text-green-800">
          「{result.tenant.name}」のアカウントを作成しました。
          ご登録メール宛に「パスワード設定 URL（3 日間有効）」付きの承認メールを送信しました。
        </div>
        <dl className="grid grid-cols-3 gap-2">
          <dt className="text-gray-500">テナントID</dt>
          <dd className="col-span-2">{result.tenant.id}</dd>
          <dt className="text-gray-500">プラン</dt>
          <dd className="col-span-2 uppercase">{result.tenant.plan}</dd>
          <dt className="text-gray-500">ログインメール</dt>
          <dd className="col-span-2 font-mono text-xs">{result.initial_user.email}</dd>
        </dl>
        <p className="text-xs text-gray-500 border-t pt-3">
          メールが届かない場合は迷惑メールフォルダもご確認ください。
          リンクが期限切れになった場合はログイン画面の「パスワードをお忘れの方」から再発行できます。
        </p>
        <div className="flex justify-end">
          <button type="button" className="btn-primary" onClick={onClose}>
            閉じる
          </button>
        </div>
      </div>
    </DialogShell>
  );
}
